import { useState, useEffect } from 'react'
import { getPredictions, PredictionsData } from '../services/predictions'
import LoadingSpinner from '../components/LoadingSpinner'

/**
 * Fase D del roadmap (10. Sistema predictivo, 11. Sistema de
 * recomendaciones). "El reto no es el algoritmo, es la estructura de
 * datos" (Sylwester) — con solo 17 días de histórico real, toda
 * proyección aquí es orientativa ("si el ritmo reciente se mantiene"),
 * no una predicción estadística. Solo se recomienda escalar un canal
 * cuando su ROI real ya es positivo: con los datos de hoy, los 4
 * canales de medios están en ROI muy negativo, así que verás
 * "Mantener" en los 4 — es el resultado correcto, no fabricamos una
 * recomendación de escalado que los datos no respaldan.
 */
const Predictions = () => {
  const [data, setData] = useState<PredictionsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      const res = await getPredictions()
      setData(res.data)
      setError(null)
    } catch (err) {
      setError('Error al cargar las predicciones')
      console.error('Error fetching predictions:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  if (loading) {
    return <LoadingSpinner />
  }

  if (error || !data) {
    return <div className="card p-6 text-center text-dashboard-muted">{error || 'Sin datos'}</div>
  }

  const cappingLabels: Record<string, { text: string; badge: string }> = {
    subir_capping: { text: 'Subir capping', badge: 'badge-success' },
    mantener_calidad_insuficiente: { text: 'Mantener (calidad insuficiente)', badge: 'badge-warning' },
    mantener_con_margen: { text: 'Mantener (con margen)', badge: 'badge-info' },
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-dashboard-text flex items-center">
          <span className="mr-3">🔮</span>
          PREDICCIONES
        </h1>
        <p className="text-dashboard-muted mt-1">
          Proyección a {data.meta.projectionHorizonDays} días "si el ritmo reciente se
          mantiene" — orientativa, no estadística, dado el histórico real disponible. Solo se
          recomienda escalar un canal cuando su ROI actual ya es positivo (más de{' '}
          {data.meta.scaleRoiThresholdPct}%), nunca por una proyección optimista.
        </p>
      </div>

      {/* Proyección por canal */}
      <div className="card">
        <div className="p-6 border-b border-dashboard-border">
          <h2 className="text-xl font-bold text-dashboard-text">Proyección de coste/revenue por canal</h2>
          <p className="text-sm text-dashboard-muted mt-1">
            Ritmo diario calculado sobre los últimos 7 días CON datos de cada serie (el gasto
            de plataforma y los leads de Leadcentre no siempre cubren el mismo rango de
            fechas).
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Canal</th>
                <th>ROI actual</th>
                <th>Ritmo diario</th>
                <th>Proyección ({data.meta.projectionHorizonDays}d)</th>
                <th>ROI proyectado</th>
                <th>Recomendación</th>
              </tr>
            </thead>
            <tbody>
              {data.channelProjections.map((c) => (
                <tr key={c.channel}>
                  <td className="font-semibold">{c.label}</td>
                  <td className={c.current.roi >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {c.current.roi.toFixed(1)}%
                    <div className="text-xs text-dashboard-muted">
                      {c.daysWithSpendData}d gasto · {c.daysWithLeadData}d leads
                    </div>
                  </td>
                  <td className="text-sm text-dashboard-muted">
                    €{c.dailyPace.spend.toFixed(2)}/día gasto
                    <div>€{c.dailyPace.revenue.toFixed(2)}/día revenue</div>
                  </td>
                  <td className="text-sm">
                    €{c.projection.spend.toLocaleString()} gasto
                    <div className="text-dashboard-muted">€{c.projection.revenue.toLocaleString()} revenue</div>
                  </td>
                  <td className={c.projection.roi >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {c.projection.roi.toFixed(1)}%
                  </td>
                  <td>
                    <span className={`badge ${c.recommendation === 'escalar' ? 'badge-success' : 'badge-warning'}`}>
                      {c.recommendation === 'escalar' ? 'Escalar' : 'Mantener'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recomendaciones de capping */}
      <div className="card">
        <div className="p-6 border-b border-dashboard-border">
          <h2 className="text-xl font-bold text-dashboard-text">
            Recomendaciones de capping ({data.cappingRecommendations.length})
          </h2>
          <p className="text-sm text-dashboard-muted mt-1">
            Solo campañas con capping configurado manualmente (Campaign Details). Recomienda
            subir el límite solo si está a punto de agotarse (≤{data.meta.cappingHeadroomDays}{' '}
            días al ritmo actual) Y la validación reciente es buena (≥
            {(data.meta.cappingMinValidationRate * 100).toFixed(0)}%) — subir el límite de una
            campaña de mala calidad no sería una recomendación razonable.
          </p>
        </div>
        {data.cappingRecommendations.length === 0 ? (
          <div className="p-6 text-center text-dashboard-muted text-sm">
            Ninguna campaña tiene capping configurado todavía
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-modern">
              <thead>
                <tr>
                  <th>Campaña</th>
                  <th>Capping</th>
                  <th>Leads hasta ahora</th>
                  <th>Proyección</th>
                  <th>Validación reciente</th>
                  <th>Recomendación</th>
                </tr>
              </thead>
              <tbody>
                {data.cappingRecommendations.map((c, i) => (
                  <tr key={i}>
                    <td className="max-w-xs truncate" title={c.campaign}>
                      {c.campaign}
                    </td>
                    <td className="font-semibold">{c.capping.toLocaleString()}</td>
                    <td>{c.leadsSoFar.toLocaleString()}</td>
                    <td className="text-sm text-dashboard-muted">
                      {c.projectedDaysToReach !== null ? `~${c.projectedDaysToReach} días` : 'Sin ritmo reciente'}
                    </td>
                    <td>{(c.avgValidationRate * 100).toFixed(1)}%</td>
                    <td>
                      <span className={`badge ${cappingLabels[c.recommendation].badge}`}>
                        {cappingLabels[c.recommendation].text}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default Predictions
